'use client';

import { useCallback, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { PageRoute, PageRoutes } from '../constants/constants';

import useCurrentMarketMarkPrice from './useCurrentMarketMarkPrice';
import {
	cleanTickerSymbol,
	formatMarketPrice,
} from '../constants/marketDecimalConfig';
import useFeatureAnnouncementGlobalState from 'src/components/FeatureAnnouncementTrigger/hooks/useFeatureAnnouncementGlobalState';

const useDocumentTitle = () => {
	const pathname = usePathname();
	const currentMarketName = useVelocityStore((s) => s.selectedMarket.name);
	const currentRoute = useVelocityStore((s) => s.currentPageRoute);

	const markPrice = useCurrentMarketMarkPrice();

	const [{ unseenCount }] = useFeatureAnnouncementGlobalState();

	const setTitleToRender = useCallback(
		(newTitle: string) => {
			if (!document?.title) return;

			if (unseenCount > 0) {
				document.title = `(${unseenCount}) ${newTitle}`;
			} else {
				document.title = newTitle;
			}
		},
		[unseenCount]
	);

	useEffect(() => {
		// check which route
		if (currentRoute) {
			if (PageRoutes.includes(currentRoute as PageRoute)) {
				switch (currentRoute as PageRoute) {
					case PageRoute.disclaimer:
						setTitleToRender('Disclaimer — Velocity');
						break;
					case PageRoute.privacy:
						setTitleToRender('Privacy Policy — Velocity');
						break;
					case PageRoute.terms:
						setTitleToRender('Terms — Velocity');
						break;
					case PageRoute.portfolio:
						setTitleToRender('Portfolio — Velocity');
						break;
					case PageRoute.funding:
						setTitleToRender('Funding — Velocity');
						break;
					case PageRoute.leaderboards:
						setTitleToRender('Leaderboard — Velocity');
						break;
					case PageRoute.redeem:
						setTitleToRender('Redeem - Velocity');
						break;
					case PageRoute.borrowLend:
						setTitleToRender('Borrow/Lend - Velocity');
						break;
					case PageRoute.stake:
						setTitleToRender('Stake - Velocity');
						break;
					case PageRoute.bridge:
						setTitleToRender(`Bridge - Velocity`);
						break;
					case PageRoute.swap:
						setTitleToRender(`Swap - Velocity`);
						break;
					case PageRoute.BAL:
						setTitleToRender(`Backstop AMM Liquidity - Velocity`);
						break;
					case PageRoute.elections:
						setTitleToRender(`Election Center - Velocity`);
						break;
					case PageRoute.vaults:
						setTitleToRender(`Vaults - Velocity`);
						break;
					case PageRoute.stats:
						setTitleToRender(`Stats - Velocity`);
						break;
					default:
						setTitleToRender('Velocity');
				}

				return;
			}
		}

		// otherwise we're at the default route
		if (markPrice && markPrice.gtZero()) {
			const cleanMarketName = currentMarketName
				? cleanTickerSymbol(currentMarketName)
				: '';
			const baseSymbol = cleanMarketName.replace('-SPOT', ''); // Get base symbol for decimal config
			const formattedPrice = formatMarketPrice(
				markPrice.toNum(),
				baseSymbol,
				true
			);
			setTitleToRender(`$${formattedPrice} | ${cleanMarketName} | Velocity`);

			return;
		}

		// otherwise just use the default title
		setTitleToRender('Velocity');
	}, [currentRoute, markPrice, pathname, setTitleToRender]);
};

export default useDocumentTitle;
